import { JSX, useState } from "react";
import MainLayout from "../components/layout/MainLayout";
import MainLayoutUser from "../components/layout/MainLayoutUser";
import MainLayoutAdmin from "../components/layout/MainLayoutAdmin";
import BannerStatic from "../components/organisms/BannerStatic";
import JobListingGrid from "../components/organisms/JobListingGrid";
import BannerJobSearch from "../components/organisms/BannerJobSearch";
import { useEffect } from "react";
import { useAuthStore } from "../store/useAuthStore";

interface TermsSection {
    title: string;
    content: JSX.Element;
}

export default function Terms() {
    const sections: TermsSection[] = [
        {
            title: "INTRODUCTION",
            content: (
                <div className="space-y-4 text-gray-700">
                    <p>
                        These Terms of Service ("Terms") regulate how you may use and access the website and mobile
                        application{" "}
                        <a
                            href="https://talentlinkindo.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline"
                        >
                            talentlinkindo.com
                        </a>
                        {" "}("Platform") owned by TalentLink. Please read them carefully as
                        these Terms affect your rights and obligations under the law. If you do not agree to these Terms,
                        please do not register an account or use our Platform. By registering an account or using our
                        Platform, you are deemed to agree and comply with everything set forth in these Terms. These
                        Terms are effective on the date these Terms are published on our Platform.
                    </p>
                </div>
            ),
        }, {
            title: "DEFINITIONS",
            content: (
                <div className="space-y-4 text-gray-700">
                    <p>Certain words and terms used in these Terms have the following meanings:</p>
                    <ul className="list-disc list-inside ml-4 space-y-2">
                        <li>
                            <strong>“Client”</strong> means a company that places, opens, and provides job vacancies or promotes work-
                            related activities on our Platform.
                        </li>
                        <li>
                            <strong>“Candidate”</strong> means an individual who registers, opens an account, and accesses the Platform's
                            services for the purpose of seeking employment.
                        </li>
                        <li>
                            <strong>“Platform”</strong> refers to all {" "}
                            <a
                                href="https://talentlinkindo.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 underline"
                            >
                                talentlinkindo.com
                            </a>
                            {" "}
                            websites and mobile applications, including but not
                            limited to websites and mobile applications for candidates as job seekers and for companies as
                            employers.
                        </li>
                        <li>
                            <strong>“You”</strong> means the Client or Candidate who accesses and uses the services on the Platform.
                        </li>
                        <li>
                            <strong>“Talentlinkindo”, </strong><strong>“We”, </strong><strong>“Us”, </strong>or <strong>“Our”</strong> refers to{" "}
                            <a
                                href="https://talentlinkindo.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 underline"
                            >
                                talentlinkindo.com
                            </a>
                            {" "}or affiliates who create,
                            operate, and have full rights to the Platform.
                        </li>
                        <li>
                            <strong>"Content"</strong> means any information and/or data that can be uploaded on the Platform.
                        </li>
                        <li>
                            <strong>"Service"</strong> means all types of services relating to providing information on job vacancies to job
                            seekers and providing data and competency information on job seekers to employers, which are
                            carried out online by the Job Portal Operator, as well as human resources recruitment
                            consulting services and outsourcing services.
                        </li>
                        <li>
                            <strong>"Account"</strong> means an identity that contains activity history, preferences, or other information
                            stored in the system.
                        </li>
                        <li>
                            <strong>"Consultant"</strong> means a professional individual working officially for TalentLink who acts as a
                            liaison to source and provide Candidates that suit the Client's needs.
                        </li>
                        <li>
                            <strong>"Intellectual Property"</strong> means all proprietary content, trademarks, service marks, brand
                            names, logos, and other intellectual property.
                        </li>
                    </ul>
                </div>
            ),

        }, {
            title: "ACCOUNT OPENING",
            content: (
                <div className="space-y-4 text-gray-700">
                    <p>When accessing the Platform, you will be directed to two options: please select Registration to
                        create an Account and/or select Login if you already have an Account. Before opening,
                        accessing, and using an Account on the Platform, you agree to these Terms, Privacy Policy, and
                        User Agreement, and voluntarily register yourself by providing the information we require. You
                        may choose the registration category you desire, namely as Jobseeker if you are a job seeker
                        ("Candidate") or Company if you are a company or employer ("Client").</p>
                    <p>By creating an Account, you agree to provide accurate, current, and complete information
                        during the registration process and to update such information to maintain its accuracy. You are
                        responsible for maintaining the confidentiality of your account credentials and all activities that
                        occur under your account.</p>

                </div>
            ),

        }, {
            title: "MOBILE APPLICATIONS AND SERVICES",
            content: (
                <div className="space-y-4 text-gray-700">
                    <p>We only provide official mobile applications on the Google Play Store, Apple App Store digital
                        markets, and our website ("Distribution Platform"), and for use only on personal computers,
                        mobile phones, or tablets. Downloading mobile applications from places other than the
                        Distribution Platform constitutes a violation of these Terms and of Our intellectual property
                        rights. To the extent you access the application via a mobile device, your wireless service
                        carrier's standard charges, data rates, and other charges may apply to you.</p>

                </div>
            ),

        }, {
            title: "ACCOUNT ACCESS AND SECURITY",
            content: (
                <div className="space-y-4 text-gray-700">
                    <p>The Platform is intended for personal use by Candidates as job seekers and Clients as employers with the aim of connecting and providing employment opportunities with Candidates. You agree not to:
                    </p>

                    <ul className="list-disc list-inside ml-4 space-y-2">
                        <li>
                            Upload, post, send, or provide content that is unlawful, harmful, threatening, abusive,
                            harassing, worrying, disturbing, tortious, defamatory, vulgar, obscene, profane, invasive of
                            another party's privacy, hateful, or contains racial or other objectionable elements;
                        </li>
                        <li>
                            Impersonate another person or entity, or otherwise misrepresent your affiliation with a person or entity;
                        </li>
                        <li>
                            Use the Platform for the benefit of a third party or in a manner not permitted by the
                            permissions granted herein;
                        </li>
                        <li>
                            Use the Platform or upload content in a way that is fraudulent, deceptive, false, misleading,
                            or deceptive;
                        </li>
                        <li>
                            Ask or require any Candidate to pay any fees, charges, costs, or money in connection with the
                            recruitment process for any job advertised on the Platform;
                        </li>
                        <li>
                            Use the Platform for commercial purposes without permission;
                        </li>
                        <li>
                            Upload, email, post, transmit, or otherwise make available any Content that you do not have
                            a right to make available under law or under contractual or fiduciary obligations (such as
                            inside information, proprietary and confidential information learned or disclosed as part of
                            employment relationships or under non-disclosure agreements);
                        </li>
                        <li>
                            Upload, email, post, transmit, or otherwise make available any Content that infringes any
                            patent, trademark, trade secret, copyright, or other proprietary right of any party;
                        </li>
                        <li>
                            Upload, email, post, transmit, or otherwise make available any unsolicited or unauthorized
                            advertising, promotional materials, "junk mail", "spam", "chain letters", "pyramid schemes",
                            or any other form of unauthorized solicitation;
                        </li>
                        <li>
                            Upload, email, post, transmit, or otherwise make available material that contains viruses,
                            worms, Trojan-horse software, or other computer code, routines, files, or programs
                            designed to directly or indirectly affect, manipulate, interrupt, destroy, or limit the
                            functionality or integrity of any computer software or hardware or data or
                            telecommunications equipment;
                        </li>
                        <li>
                            Perform actions or engage in behavior that can directly or indirectly damage, disable,
                            overburden, or disrupt the Platform or servers or networks connected to the Platform;
                        </li>
                        <li>
                            Use the Platform to intentionally or unintentionally violate any applicable local, state,
                            national, or international laws, rules, codes, directives, guidelines, policies, or regulations
                            including, without limitation, laws and requirements (whether legally enforceable or not)
                            relating to anti-money laundering or anti-terrorism;
                        </li>
                        <li>
                            Use the Platform to invade another party's privacy or to "stalk" or harass another party;
                        </li>
                        <li>
                            Use the Platform for purposes that are deemed to be and/or violate applicable law.
                        </li>
                    </ul>


                </div>
            ),

        }, {
            title: "AGREEMENT",
            content: (
                <div className="space-y-4 text-gray-700">
                    <p>By accessing the Platform, you understand that your account and all content that you upload
are entirely your responsibility. Therefore, you agree to:
                    </p>

                    <ul className="list-disc list-inside ml-4 space-y-2">
                        <li>
                            Keep your password confidential and only use your email and password when logging in;
                        </li>
                        <li>
                            Ensure that you exit from your account at the end of each session on the Platform;
                        </li>
                        <li>
                            Immediately notify us of any unauthorized use of your account, email, and/or password;
                        </li>
                        <li>
                            Ensure that your account information is accurate and up to date.
                        </li>
                    </ul>
                    <p>You are fully responsible for all activities carried out using your email and account, even if such
activities or use are not carried out by you. We will not be liable for any loss or damage arising
from the unauthorized use of your password or your failure to comply with this Section.
                    </p>
                    <p>You are expressly prohibited from trading your Account or transferring ownership of your
Account to a third party. Buying and selling Accounts and transferring Account ownership to
third parties (except transfers of Account ownership due to the transfer of ownership of the
business entity that owns the Account carried out legally according to applicable law)
constitutes a violation of these Terms.
                    </p>


                </div>
            ),

        }, {
            title: "TERMINATION AND TEMPORARY SUSPENSION",
            content: (
                <div className="space-y-4 text-gray-700">
                    <p>We may, at any time, temporarily suspend (without a certain time limit or within a period
deemed appropriate by Us) or may terminate (including terminating after a temporary
suspension) any or all services on the Platform after the following events occur, by providing
prior written notice to the Client:
                    </p>

                    <ul className="list-disc list-inside ml-4 space-y-2">
                        <li>
                            If We have reviewed and determined that the Client (including the Client's employees) has
used, attempted to use, or is likely to use the Platform in violation of the laws and regulations
or these Terms or any Agreement, or in any way that has the potential to or may cause
penalties, embarrassment, chaos, or disruption in any form that is detrimental to the
interests of TalentLink and its affiliates;
                        </li>
                        <li>
                            If the Client has breached a material obligation or is in default of any Terms or Agreement
with TalentLink and its affiliates;
                        </li>
                        <li>
                            If a shareholder circular resolution or action for proceedings for insolvency, winding up,
bankruptcy, or judicial management has been commenced in any court with jurisdictional
competence, has been passed or filed against the Client, or for any other reason the Client
may be deemed to be insolvent (including if an administrator or curator or similar official has
been appointed over all or part of the Client's assets, or the Client has generally entered into
or proposed a composition or plan with its creditors, or a similar event occurs in any
applicable jurisdiction);
                        </li>
                        <li>
                            If an action has been taken by a creditor of the Client to recover, realize, or secure by
enforcing any security over any asset of the Client or to enforce any judgment against the
Client;
                        </li>
                        <li>
                            If the Client is no longer established, registered, or in good standing
                        </li>
                    </ul>


                </div>
            ),

        },
        // section lain bisa ditambahkan sama caranya
    ];

    const [activeIndex, setActiveIndex] = useState(0);

    const { accessToken, type } = useAuthStore();
    useEffect(() => {
        document.title = "TalentLink | Terms Condition";

        const metaDescription = document.querySelector("meta[name='description']");
        if (metaDescription) {
            metaDescription.setAttribute("content", "Halaman utama TalentLink");
        }
    }, []);

    const Layout =
        !accessToken
            ? MainLayout
            : type === "candidate"
                ? MainLayoutUser
                : type === "company"
                    ? MainLayoutAdmin
                    : MainLayout;

    return (
        <Layout>

            <BannerStatic blur>
                <h2 className="text-3xl md:text-5xl font-medium mb-4 md:w-3/4 lg:w-2/3 text-shadow">
                    TERMS OF SERVICE
                </h2>
                <p className="text-base md:text-xl font-light mb-8 w-full md:w-3/4 lg:w-2/3 text-shadow">
                    Effective Date: January 7, 2026
                </p>
            </BannerStatic>
            <div className="container mx-auto px-6 pt-6 flex gap-8 flex-col-reverse md:flex-row">

                {/* Sidebar kiri */}
                <div className="md:w-1/4 mb-6 md:mb-0">
                    <ul className="space-y-2 border rounded-lg p-4 bg-white shadow">
                        {sections.map((section, idx) => (
                            <li
                                key={idx}
                                onClick={() => setActiveIndex(idx)}
                                className={`cursor-pointer p-2 rounded-lg ${activeIndex === idx ? "bg-blue-100 text-blue-600 font-semibold" : "text-gray-800 hover:bg-gray-100"
                                    }`}
                            >
                                {section.title}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Konten kanan */}
                <div className="md:w-3/4 bg-white p-6 rounded shadow border h-[430px] overflow-y-auto">
                    <h2 className="text-xl font-semibold mb-4">{sections[activeIndex].title}</h2>
                    <div>{sections[activeIndex].content}</div>
                </div>
            </div>

        </Layout>
    );
}
